import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useFinancialContexts } from '../../contexts/ContextsContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigationIntent } from '../../hooks/useNavigationIntent';
import { canManageContext } from '../../utils/contextPermissions';
import { computeBudget } from '../../utils/budget';
import { monthKey, monthLabel } from '../../utils/calendar';
import { chart, formatMoney } from '../../theme/chart';
import { PlanningCard } from './PlanningCard';
import { BudgetRing } from './BudgetRing';

const Swatch = ({ background }: { background: string }) => (
  <Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '2px', background, mr: 0.75 }} />
);

interface Props {
  viewMonth: Date;
  spent: number;
  committed: number;
  currency: string;
}

export const BudgetStatusCard: React.FC<Props> = ({ viewMonth, spent, committed, currency }) => {
  const { t, i18n } = useTranslation('planning');
  const { selectedContext, contexts, updateContext } = useFinancialContexts();
  const { state: authState } = useAuth();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [limitInput, setLimitInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Personal view: the budget lives on the user's own personal context.
  const targetContext =
    selectedContext ??
    contexts.find(
      (context) => context.type === 'personal' && context.ownerId === authState.user?.id,
    );

  const limit = Number(targetContext?.settings?.monthlyBudget) || 0;
  const canEdit = selectedContext
    ? canManageContext(selectedContext.memberRole)
    : Boolean(targetContext);

  const budget = computeBudget({ spent, committed, limit });

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat(i18n.language, { style: 'currency', currency }).format(amount);
  const money = (amount: number) => formatMoney(amount, currency, i18n.language);

  // The pace tick only means something in the month that is still running.
  const now = new Date();
  const isCurrentMonth = monthKey(viewMonth) === monthKey(now);
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const pacePct = isCurrentMonth ? now.getDate() / daysInMonth : undefined;
  const over = budget.overBy > 0;
  const hue = over ? chart.spend : chart.ok;
  const hatch = `repeating-linear-gradient(45deg, ${hue}99 0 4px, transparent 4px 8px)`;

  const openDialog = () => {
    setLimitInput(limit ? String(limit) : '');
    setError(null);
    setDialogOpen(true);
  };

  // "Definir o orçamento", from the first steps on Início or from Fin.
  useNavigationIntent('open', 'budget', () => {
    if (canEdit) openDialog();
  });

  const saveLimit = async () => {
    if (!targetContext) return;
    const value = limitInput.trim() === '' ? 0 : parseFloat(limitInput);
    if (Number.isNaN(value) || value < 0) {
      setError(t('budget.invalidLimit'));
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const settings = { ...(targetContext.settings ?? {}) };
      if (value > 0) {
        settings.monthlyBudget = value;
      } else {
        delete settings.monthlyBudget;
      }
      await updateContext(targetContext.id, { settings });
      setDialogOpen(false);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PlanningCard
      title={t('budget.title')}
      aside={
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography sx={{ fontSize: 12, color: chart.inkMuted }}>{monthLabel(viewMonth, i18n.language)}</Typography>
          {canEdit && budget.hasLimit && (
            <Tooltip title={t('budget.editLimit') as string}>
              <IconButton size="small" onClick={openDialog} aria-label={t('budget.editLimit') as string} sx={{ p: 0.5 }}>
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      }
    >
        {!budget.hasLimit ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              gap: 2,
              py: 3,
            }}
          >
            <Typography sx={{ fontSize: 13, color: chart.inkMuted, maxWidth: 300 }}>
              {t('budget.noLimit')}
            </Typography>
            {canEdit && (
              <Button variant="outlined" size="small" onClick={openDialog} sx={{ textTransform: 'none', borderRadius: 2 }}>
                {t('budget.setLimit')}
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
            <BudgetRing
              spentPct={budget.spentPct}
              committedPct={budget.committedPct}
              hue={hue}
              pacePct={pacePct}
              headline={money(over ? budget.overBy : budget.remaining)}
              subline={over ? t('budget.overOf', { amount: money(limit) }) : t('budget.leftOf', { amount: money(limit) })}
              ariaLabel={
                t('budget.barLabel', {
                  spent: formatCurrency(spent),
                  committed: formatCurrency(committed),
                  remaining: formatCurrency(budget.remaining),
                }) as string
              }
            />
            <Typography sx={{ fontSize: 12, color: chart.inkSecondary }}>
              {isCurrentMonth
                ? t('budget.pace', { used: Math.round(budget.spentPct), month: Math.round((pacePct ?? 0) * 100) })
                : t('budget.used', { used: Math.round(budget.spentPct) })}
            </Typography>
            <Stack direction="row" flexWrap="wrap" justifyContent="center" columnGap={2} rowGap={0.5} sx={{ fontSize: 12, color: chart.inkSecondary }}>
              <span><Swatch background={hue} />{t('budget.spent')} {money(spent)}</span>
              <span><Swatch background={hatch} />{t('budget.committed')} {money(committed)}</span>
              <span><Swatch background={chart.track} />{t('budget.remaining')} {money(budget.remaining)}</span>
            </Stack>
          </Box>
        )}

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{t('budget.dialogTitle')}</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              label={t('budget.dialogLabel')}
              value={limitInput}
              onChange={(event) => setLimitInput(event.target.value)}
              fullWidth
              autoFocus
              type="number"
              inputProps={{ min: 0, step: 50 }}
              sx={{ mt: 1 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              {t('budget.dialogHint')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} disabled={isSaving}>
              {t('budget.cancel')}
            </Button>
            <Button onClick={saveLimit} variant="contained" disabled={isSaving}>
              {t('budget.save')}
            </Button>
          </DialogActions>
        </Dialog>
    </PlanningCard>
  );
};
